<template>
    <div>
      <div v-if="loading" class="d-flex justify-content-center align-items-center flex-direction-column">
        <i class="el-icon-loading" style="font-size: 20px"></i>
        <span class="mt-2">{{ $t('product loading...') }}</span>
      </div>
      <div v-else-if="notFound" class="d-flex justify-content-center align-items-center flex-direction-column">
        <span class="mt-2">{{ $t('product not found') }}</span>
      </div>
      <div v-else>
        <h2 class="product-title mt-0">{{ product.name_english || product.name }}</h2>
        <div class="product-content">
          <div class="product-content__carousel">
            <el-carousel
              v-if="
                product.sources.assets &&
                product.sources.assets.thumbnail &&
                product.sources.assets.thumbnail.images &&
                product.sources.assets.thumbnail.images.length"
              arrow="always"
              indicator-position="none"
              :autoplay="false"
            >
              <el-carousel-item
                v-for="asset in product.assets.data.filter((item) => item.url)"
                :key="asset.id"
              >
                <img
                  v-if="asset.kind === 'image'"
                  :src="asset.thumbnail" style="max-width: 100%; max-height: 100%;"
                  @click="$emit('showFullContent', {type: 'image', data: asset.url})"
                />
                <div
                  v-else-if="asset.kind === 'youtube'"
                  class="video-item"
                  :style="{backgroundImage: `url(${encodeURI(asset.thumbnail)})`}"
                  @click="$emit('showFullContent', {type: 'youtube', data: asset.url})">
                  <i class="el-icon-caret-right"></i>
                </div>
                <iframe
                  v-else-if="asset.kind === 'vimeo'"
                  :src="`https://player.vimeo.com/video/${asset.url}?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`"
                  style="border: none"
                  @click="$emit('showFullContent', {type: 'vimeo', data: asset.url})">
                ></iframe>
              </el-carousel-item>
              <el-carousel-item v-if="!product.assets.data.filter((item) => item.url).length && product.sources.assets.thumbnail.primary && product.sources.assets.thumbnail.primary.link">
                <img
                  :src="product.sources.assets.thumbnail.primary.link"
                  style="max-width: 100%; max-height: 100%;"
                  @click="$emit('showFullContent', {type: 'image', data: product.sources.assets.thumbnail.primary.link})"
                />
              </el-carousel-item>
              <el-carousel-item
                v-for="image in product.sources.assets.thumbnail.images.filter((item) => item.link)"
                :key="image.id"
              >
                <img :src="image.link" style="max-width: 100%; max-height: 100%;" @click="$emit('showFullContent', {type: 'image', data: image.link})" />
              </el-carousel-item>
            </el-carousel>
          </div>
          <div class="product-content__text">
            <div class="text-bold d-flex justify-content-space-between mb-2 mt-2">
              <span class="mr-4">Code: {{ product.sku }}</span>
              <span>Color: {{ product.collection }}</span>
            </div>
            <div v-if="variant" class="d-flex justify-content-space-between align-items-center mb-2">
              <span class="mr-2 text-bold">{{ $t('Price') }}</span>
              <span class="product-price">{{ variant.price | yen }}</span>
            </div>
            <div class="product-description">
              <p v-html="product.description"></p>
              <div class="blur-continue"></div>
            </div>
            <div v-if="product.option_data && Object.keys(product.option_data).length" class="product-options mt-2">
              <div
                v-for="(value, key) in product.option_data"
                :key="key"
                class="product-options__item d-flex justify-content-end align-items-center mb-1"
              >
                <span class="text-capitalize mr-2">{{ key }}</span>
                <el-select v-model="order[key]" :size="inputSize" placeholder="Select" @change="changeOption(key, value, $event)">
                  <el-option
                    v-for="(option, optionKey) in value.options"
                    :key="optionKey"
                    :label="option.handle"
                    :value="option.values.en">
                  </el-option>
                </el-select>
              </div>
              <div v-if="cartAvailable" class="product-options__item d-flex justify-content-end align-items-center mb-1">
                <span class="mr-2">
                  {{ $t('Quantity on stock') }}
                  <i v-if="inventoryChecking" class="el-icon-loading"></i>
                  <i
                    v-else
                    class="el-icon-refresh"
                    style="cursor: pointer"
                    v-tooltip="'Reload quantity in stock'"
                    @click="reloadInventory(variant.id)"
                  ></i>
                </span>
                <span class="product-inventory">{{ variant.inventory }}</span>
              </div>
            </div>
            <div v-if="cartAvailable && variant" class="add-to-cart d-flex justify-content-end align-items-center mt-2">
              <el-input-number
                v-model="order.quantity"
                :min="1"
                :max="Math.min((variant.inventory || 10), (variant.max_qty || 10))"
                :disabled="!variant.id || !variant.inventory"
                :size="buttonSize"
                class="mr-2"
              ></el-input-number>
              <el-button
                type="primary"
                :size="buttonSize"
                :disabled="!variant.id || !variant.inventory || inventoryChecking"
                @click="submitOrder"
              >{{ $t('Add to cart') }}</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>
<script>
import axios from 'axios'
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'Product',
  props: {
    productId: {
      type: String,
      require: true
    },
    cartAvailable: {
      default: false
    }
  },
  computed: {
    ...mapGetters('preview', ['variant'])
  },
  data () {
    return {
      buttonSize: screen.height > 780 ? 'medium' : 'small',
      inputSize: screen.height > 780 ? 'small' : 'mini',
      loading: true,
      notFound: false,
      inventoryChecking: false,
      product: null,
      order: {
        quantity: 1,
        price: null,
        inventory_quantity: null
      },
      variantSelected: {
        inventory: '',
        price: '',
        max_qty: 10
      }
    }
  },
  methods: {
    ...mapActions('preview', ['setVariant', 'checkVariantInventory']),
    fetchProduct () {
      this.notFound = false
      this.loading = true
      let prefixUrl = this.productId.length > 6 ? `sku/${this.productId}` : this.productId
      axios.get(`${process.env.OLD_ECOMMERCE_URL}/products/${prefixUrl}?&include=assets,variants`)
        .then((response) => {
          this.loading = false
          if (response.data) {
            this.product = response.data
            if (response.data.variants.data.length > 0) {
              let variant = response.data.variants.data[0]
              this.setVariant(variant)
              for (const key in variant.options) {
                this.order[key] = variant.options[key].en
              }
              this.$emit('variantSelected', variant)
            }
            this.$emit('productFetched', response.data)
          } else {
            this.notFound = true
          }
        })
        .catch(() => {
          this.loading = false
          this.notFound = true
        })
    },
    changeOption (optionKey, option, value) {
      this.$set(this.order, optionKey, value)
      this.$forceUpdate()
      this.calculatePrice()
    },
    calculatePrice () {
      let variantSelected = this.product.variants.data.find((variant) => {
        for (const [key, value] of Object.entries(this.product.option_data)) {
          console.log(`${variant.options[key]['en']}: ${this.order[key]}`)
          if (!variant.options[key]['en'] || !this.order[key]) return false
          if (variant.options[key]['en'].toLowerCase() !== this.order[key].toLowerCase()) return false
        }
        return true
      })
      this.setVariant(variantSelected)
      this.$emit('variantSelected', variantSelected)
    },
    async submitOrder () {
      if (this.variant && this.order.quantity) {
        this.inventoryChecking = true
        let inventoryAvailable = await this.checkVariantInventory({variantId: this.variant.id, quantity: this.order.quantity})
        this.inventoryChecking = false
        if (inventoryAvailable) {
          this.$emit('submitOrder', {quantity: this.order.quantity, variant: this.variant, assets: this.product.assets.data})
          // reset order quantity
          this.$set(this.order, 'quantity', 1)
        } else {
          this.$notify({
            type: 'warning',
            title: 'Insufficient quantity in stock',
            message: 'Someone just picked some of this'
          })
        }
      }
    },
    reloadInventory (variantId) {
      this.inventoryChecking = true
      this.checkVariantInventory({variantId, quantity: 1})
        .then((response) => {
          this.inventoryChecking = false
        })
    }
  },
  mounted () {
    this.fetchProduct()
  },
  watch: {
    productId () {
      if (!this.loading && this.productId) {
        this.fetchProduct()
      }
    }
  }
}
</script>
<style lang="css" scoped>
.text-bold {
  font-weight: bold;
}
.product-description {
  max-height: calc(var(--vh, 1hv) * 10);
  overflow: auto;
  position: relative;
}
.blur-continue {
  position: absolute;
  bottom: -10px;
  left: 0px;
  height: 20px;
  /* background-image: linear-gradient(transparent, #fff); */
  width: 100%;
}
.text-capitalize {
  text-transform: capitalize;
}
.el-carousel__item {
  display: flex;
  justify-content: center;
}
.product-price {
  line-height: 36px;
  height: 36px;
  width: 50%;
  text-align: center;
  padding: 0px 4px;
  border: 1px solid var(--color-theme);
  border-radius: 2px;
}
.product-inventory {
  line-height: 30px;
  height: 30px;
  width: calc(50% - 18px);
  text-align: end;
  padding: 0px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
}
.video-item {
  width: 100%;
  height: 100%;
  position: relative;
  background-size: cover;
  background-repeat: no-repeat;
  background-color: transparent;
  background-position: center center;
  transition: all .3s ease-in-out;
}
.video-item i {
  position: absolute;
  top: 50%;
  left: 50%;
  color: #fff;
  cursor: pointer;
  font-size: 40px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background-color: rgb(0, 0, 0, 0.5);
}
.add-to-cart .el-input-number {
  max-width: 200px;
  width: 50%;
}
.add-to-cart .el-button {
  max-width: 200px;
  width: 50%;
}
@media only screen and (max-height: 480px) {
  .product-description {
    max-height: 60px;
  }
  .product-title {
    font-size: 18px;
  }
  .product-content .el-carousel__container {
    max-height: 50vh;
  }
  .product-content .el-input__inner {
    height: 28px;
  }
  .product-price {
    height: 26px;
    line-height: 26px;
  }
  .product-inventory {
    height: 26px;
  }
}
@media (orientation: landscape) and (max-height: 768px) {
  .product-content {
    display: flex;
  }
  .product-content__carousel {
    width: 50%;
    margin-right: 8px;
  }
  .product-content__text {
    width: 50%;
  }
  .product-content .el-carousel__container {
    max-height: 50vw;
  }
  .product-description {
    max-height: 20vh;
  }

  @media only screen and (max-height: 780px) {
    .product-price {
      height: 26px;
      line-height: 26px;
    }
    .product-inventory {
      height: 26px;
    }
  }
}
</style>
<style lang="css">
.product-popup .el-carousel__container {
  max-height: 30vh;
}
.product-options .el-select {
  width: calc(50% + 8px);
}
@media (orientation: landscape) and (max-height: 768px) {
  .product-popup {
    max-height: 96px;
  }
  .product-content .el-carousel__container {
    max-height: 50vh;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/preview/Product.vue